import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { MapPin, Building2, ArrowRight, Users } from "lucide-react";

export default function NetworkSector() {
  const { category, city } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    const p = new URLSearchParams();
    if (category) p.set("category", category);
    if (city) p.set("city", city);
    api.get(`/public/network/sector?${p.toString()}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [category, city]);

  useEffect(() => {
    const unknown = Boolean(category) && data && !data.category;
    let m = document.head.querySelector('meta[name="robots"]');
    if (unknown) {
      if (!m) { m = document.createElement("meta"); m.setAttribute("name", "robots"); document.head.appendChild(m); }
      m.setAttribute("content", "noindex,follow");
    }
    return () => { if (m) m.setAttribute("content", "index,follow"); };
  }, [category, data]);

  const catName = data?.category?.name || "Professionnels";
  const title = city ? `${catName} à ${city} (28)` : `${catName} en Eure-et-Loir (28)`;
  useSeo({
    title: `${title} · Réseau Pro PROXIreduc-28`,
    description: `Trouvez les ${catName.toLowerCase()} ${city ? `à ${city}` : "en Eure-et-Loir"} membres du Réseau Pro local PROXIreduc-28. Coordonnées, avis et avantages locaux.`,
  });

  if (data === false) return <div className="max-w-4xl mx-auto p-8 text-slate-500">Secteur introuvable.</div>;
  const notFound = Boolean(category) && data && !data.category;
  if (notFound) {
    return (
      <div className="max-w-4xl mx-auto p-12 text-center" data-testid="sector-notfound">
        <h1 className="font-heading text-2xl font-extrabold text-slate-900">Ce secteur n'existe pas</h1>
        <p className="text-slate-500 mt-2">Découvrez tous les secteurs du Réseau Pro local.</p>
        <Link to="/reseau-pro" className="inline-block mt-4 text-[#E05638] font-semibold underline">Voir le Réseau Pro local</Link>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen">
      <section className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#ffb59e] mb-3"><Users size={14} />Réseau Pro local · Eure-et-Loir</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold" data-testid="sector-h1">{title}</h1>
          <p className="text-slate-300 mt-3 max-w-2xl">{data?.total || 0} professionnel(s) référencé(s){city ? ` à ${city}` : " dans le département"}. Contactez-les et profitez d'avantages locaux.</p>
          <Link to="/inscription?role=professional" className="inline-flex items-center gap-2 bg-[#E05638] hover:bg-[#c94a2f] text-white font-semibold px-5 py-2.5 rounded-xl mt-5 transition-colors" data-testid="sector-join">Vous êtes pro ? Rejoignez gratuitement <ArrowRight size={15} /></Link>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 py-10">
        {!data ? (
          <div className="text-slate-400">Chargement…</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sector-list">
            {data.items.map((b) => (
              <Link key={b.id} to={`/commerce/${b.id}`} className="bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-md hover:border-[#E05638]/40 transition-all flex items-center gap-3" data-testid={`sector-card-${b.id}`}>
                {b.logo ? <img src={b.logo} alt={b.name} className="w-12 h-12 rounded-xl object-cover" /> : <div className="w-12 h-12 rounded-xl bg-slate-100 grid place-items-center text-slate-400"><Building2 size={22} /></div>}
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{b.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin size={11} />{b.city}{b.category_name ? ` · ${b.category_name}` : ""}</div>
                </div>
              </Link>
            ))}
            {data.items.length === 0 && <div className="text-slate-400 col-span-full">Aucun professionnel pour ce secteur pour le moment.</div>}
          </div>
        )}

        {data?.cities?.length > 0 && data.category && (
          <div className="mt-10">
            <h2 className="font-heading font-extrabold text-slate-900 mb-3">{catName} par ville</h2>
            <div className="flex flex-wrap gap-2">
              {data.cities.map((c) => (
                <Link key={c} to={`/reseau-pro/${data.category.slug}/${encodeURIComponent(c)}`} className="text-sm px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-[#E05638] text-slate-700" data-testid={`sector-city-${c}`}>{catName} à {c}</Link>
              ))}
            </div>
          </div>
        )}

        {data?.sectors?.length > 0 && (
          <div className="mt-10">
            <h2 className="font-heading font-extrabold text-slate-900 mb-3">Autres secteurs</h2>
            <div className="flex flex-wrap gap-2">
              {data.sectors.filter((s) => !data.category || s.id !== data.category.id).map((s) => (
                <Link key={s.id} to={`/reseau-pro/${s.slug}`} className="text-sm px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-[#E05638] text-slate-700">{s.name}</Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
